/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * ClientAnalytics - Performance analytics charts for coach's client view
 * Uses CSS-based visualizations (no external chart library)
 */

import { TrendingUp, BarChart3, Clock, Target, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'

// Simple bar for progress visualization
const ProgressBar = ({ value, max = 100, color = 'primary', label, showValue = true }) => {
  const percentage = max > 0 ? Math.min(100, (value / max) * 100) : 0
  const colorClasses = {
    primary: 'bg-primary-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500'
  }

  return (
    <div className="flex items-center gap-3">
      {label && (
        <span className="text-sm text-gray-600 dark:text-gray-400 w-24 truncate">{label}</span>
      )}
      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showValue && (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12 text-right">
          {typeof value === 'number' ? value.toFixed(0) : value}%
        </span>
      )}
    </div>
  )
}

// Simple vertical bar chart
const BarChart = ({ data, height = 120, barColor = 'primary' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500">
        No data available
      </div>
    )
  }

  const maxValue = Math.max(...data.map((d) => d.value), 1)
  const colorClasses = {
    primary: 'bg-primary-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500'
  }

  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((item, idx) => {
        const barHeight = (item.value / maxValue) * 100
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={`w-full rounded-t transition-all duration-300 ${colorClasses[barColor]} hover:opacity-80`}
              style={{ height: `${barHeight}%`, minHeight: item.value > 0 ? '4px' : '0' }}
              title={`${item.label}: ${item.value}%`}
            />
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate w-full text-center">
              {item.label}
            </span>
          </div>
        )
      })}
    </div>
  )
}

const ClientAnalytics = ({ challenges, stats }) => {
  const [expandedSection, setExpandedSection] = useState('scores')

  if (!challenges || challenges.length === 0) {
    return null
  }

  // Calculate analytics from challenges
  const completedChallenges = challenges.filter(
    (c) => c.status === 'completed' && c.totalScore !== null
  )

  // Recent scores trend (last 10 challenges)
  const recentScores = completedChallenges
    .slice(0, 10)
    .reverse()
    .map((c, idx) => ({
      label: `#${idx + 1}`,
      value: c.totalScore || 0
    }))

  // Score distribution
  const scoreRanges = [
    { label: '90-100%', min: 90, max: 100, count: 0 },
    { label: '80-89%', min: 80, max: 89.99, count: 0 },
    { label: '70-79%', min: 70, max: 79.99, count: 0 },
    { label: '60-69%', min: 60, max: 69.99, count: 0 },
    { label: '<60%', min: 0, max: 59.99, count: 0 }
  ]

  completedChallenges.forEach((c) => {
    const score = c.totalScore || 0
    const range = scoreRanges.find((r) => score >= r.min && score <= r.max)
    if (range) range.count++
  })

  const maxScoreCount = Math.max(...scoreRanges.map((r) => r.count), 1)

  // Time per challenge (average)
  const avgTimePerChallenge =
    completedChallenges.length > 0
      ? completedChallenges.reduce((sum, c) => sum + (c.totalTime || 0), 0) /
        completedChallenges.length
      : 0

  // Calculate performance metrics
  const avgScore =
    stats?.averageScore ??
    (completedChallenges.length > 0
      ? completedChallenges.reduce((sum, c) => sum + (c.totalScore || 0), 0) /
        completedChallenges.length
      : 0)

  const accuracyRate =
    stats?.accuracyRate ??
    (stats?.totalQuestionsAnswered > 0
      ? (stats.totalCorrectAnswers / stats.totalQuestionsAnswered) * 100
      : 0)

  // Performance trend (compare first half to second half of recent challenges)
  let performanceTrend = 0
  if (recentScores.length >= 4) {
    const halfPoint = Math.floor(recentScores.length / 2)
    const firstHalf = recentScores.slice(0, halfPoint)
    const secondHalf = recentScores.slice(halfPoint)
    const firstAvg = firstHalf.reduce((sum, s) => sum + s.value, 0) / firstHalf.length
    const secondAvg = secondHalf.reduce((sum, s) => sum + s.value, 0) / secondHalf.length
    performanceTrend = secondAvg - firstAvg
  }

  const formatTime = (seconds) => {
    if (!seconds) return '0m'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  return (
    <div className="card">
      <div className="card-body">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          Performance Analytics
        </h2>

        {/* Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Score</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {avgScore > 0 ? `${avgScore.toFixed(1)}%` : 'N/A'}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Accuracy</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {accuracyRate > 0 ? `${accuracyRate.toFixed(1)}%` : 'N/A'}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Time</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {formatTime(avgTimePerChallenge)}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Trend</p>
            <div className="flex items-center gap-1">
              {performanceTrend > 0 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-xl font-bold text-green-600 dark:text-green-400">
                    +{performanceTrend.toFixed(1)}
                  </span>
                </>
              ) : performanceTrend < 0 ? (
                <>
                  <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />
                  <span className="text-xl font-bold text-red-600 dark:text-red-400">
                    {performanceTrend.toFixed(1)}
                  </span>
                </>
              ) : (
                <span className="text-xl font-bold text-gray-500 dark:text-gray-400">-</span>
              )}
            </div>
          </div>
        </div>

        {/* Expandable Sections */}
        <div className="space-y-3">
          {/* Score Trend Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('scores')}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-gray-900 dark:text-gray-100">Score Trend</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  (Last {recentScores.length} challenges)
                </span>
              </div>
              {expandedSection === 'scores' ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 'scores' && (
              <div className="p-4 pt-0">
                <BarChart data={recentScores} barColor="blue" />
              </div>
            )}
          </div>

          {/* Score Distribution Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('distribution')}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-500" />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  Score Distribution
                </span>
              </div>
              {expandedSection === 'distribution' ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 'distribution' && (
              <div className="p-4 pt-0 space-y-2">
                {scoreRanges.map((range) => (
                  <div key={range.label} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-16">
                      {range.label}
                    </span>
                    <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          range.min >= 90
                            ? 'bg-green-500'
                            : range.min >= 70
                              ? 'bg-blue-500'
                              : range.min >= 60
                                ? 'bg-amber-500'
                                : 'bg-red-500'
                        }`}
                        style={{ width: `${(range.count / maxScoreCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-8 text-right">
                      {range.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Time Analysis Section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              onClick={() => toggleSection('time')}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                <span className="font-medium text-gray-900 dark:text-gray-100">Time Analysis</span>
              </div>
              {expandedSection === 'time' ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedSection === 'time' && (
              <div className="p-4 pt-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      Total Time Spent
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatTime(stats?.totalTimeSpent || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      Avg per Challenge
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatTime(avgTimePerChallenge)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Time per Recent Challenge
                  </p>
                  <BarChart
                    data={completedChallenges
                      .slice(0, 10)
                      .reverse()
                      .map((c, idx) => ({
                        label: `#${idx + 1}`,
                        value: Math.round((c.totalTime || 0) / 60) // Convert to minutes for display
                      }))}
                    barColor="amber"
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center">
                    (minutes)
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientAnalytics
